/**
 * Super Max (2026-09-03 owner ask): the fourth Mega Level only exists for
 * the Megas Niantic has released it for, so a form shows THREE level circles
 * until then and four after. Three sources say a species has it, any one
 * is enough:
 *
 *   1. the curated list below (what the owner has seen in the game);
 *   2. the admin Dex Config release schedule under the "superMax" axis
 *      (`activeAddSet`, date-aware, so a scheduled release flips on its own);
 *   3. the live event feed: an event whose name says "Super Max" and lists
 *      the species, from the moment it starts.
 *
 * Species-wide: a pair (X / Y) releases together in GO.
 */
import { activeAddSet, removeSet } from "./releaseSchedule";
import { SUPER_MAX_UNLOCK } from "./generated/megaMoves.generated";

/** Release-schedule axis the admin page edits for Super Max. */
export const SUPER_MAX_VARIANT_KEY = "superMax";

/** Species with Super Max released, as seen in the game (owner, 2026-09-03: Mewtwo). */
export const SUPER_MAX_CURATED: ReadonlySet<number> = new Set<number>([150]);

const SUPER_MAX_EVENT = /\bsuper\s*max\b/i;

export interface SuperMaxFeedEvent {
  name: string;
  /** ISO start; the release counts from the moment it begins. */
  start: string;
  spawns?: ReadonlyArray<{ speciesId?: number | null }>;
}

/** Species a feed names in a started "Super Max" event. */
export function superMaxSpeciesFromFeed(
  events: readonly SuperMaxFeedEvent[] | null | undefined,
  now = Date.now(),
): Set<number> {
  const out = new Set<number>();
  for (const e of events ?? []) {
    if (!SUPER_MAX_EVENT.test(e.name)) continue;
    const start = Date.parse(e.start);
    if (Number.isFinite(start) && start > now) continue;
    for (const s of e.spawns ?? []) if (s.speciesId != null) out.add(s.speciesId);
  }
  return out;
}

/**
 * Whether Super Max is released for the species: curated or scheduled or
 * announced by a started event, unless the admin has forced it off.
 */
export function superMaxReleased(
  dexId: number,
  feedSpecies: ReadonlySet<number> = new Set(),
  now = Date.now(),
): boolean {
  if (removeSet(SUPER_MAX_VARIANT_KEY).has(dexId)) return false;
  if (SUPER_MAX_CURATED.has(dexId)) return true;
  // The Game Master names the species that can reach Super Max (a
  // MEGA_EVOLUTION_LEVEL_4 template each, generated with the Mega moves):
  // Raichu, Victreebel, Starmie, Dragonite, Mewtwo, Skarmory, Chesnaught,
  // Delphox, Greninja, Malamar, Falinks as of 2026-09-04. Owner report: a
  // Max-level Mega Raichu read "Super Max is for designated Pokémon".
  if (SUPER_MAX_UNLOCK[dexId] != null) return true;
  if (activeAddSet(SUPER_MAX_VARIANT_KEY).has(dexId)) return true;
  void now;
  return feedSpecies.has(dexId);
}

/** The highest level a species' Megas can show: 4 with Super Max, else 3. */
export function megaMaxLevel(dexId: number, feedSpecies?: ReadonlySet<number>): 3 | 4 {
  return superMaxReleased(dexId, feedSpecies) ? 4 : 3;
}
